import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Collapsible, CollapsibleContent } from '../../components/ui/collapsible';
import { Filter, ChevronDown, ChevronUp, X } from 'lucide-react';

const DashboardFilters = ({ filters, setFilters, filterOptions, showFinishQuality = false, showLRStatus = false }) => {
  const [showFilters, setShowFilters] = React.useState(false);
  const { suppliers = [], qualities = [], items = [], subqualities = [] } = filterOptions;

  const resetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      supplier_id: '',
      quality_id: '',
      subquality_id: '',
      item_id: '',
      status: '',
      lr_status_filter: ''
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <div className="mb-4">
      <Button
        data-testid="dashboard-filter-toggle"
        variant="outline"
        onClick={() => setShowFilters(!showFilters)}
        className="w-fit"
      >
        <Filter className="h-4 w-4 mr-2" />
        Filters
        {activeFilterCount > 0 && (
          <Badge className="ml-2 bg-slate-900 text-white">{activeFilterCount}</Badge>
        )}
        {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
      </Button>

      <Collapsible open={showFilters} onOpenChange={setShowFilters}>
        <CollapsibleContent>
          <Card className="mt-3 border border-slate-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Date From</Label>
                  <Input
                    data-testid="filter-date-from"
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date To</Label>
                  <Input
                    data-testid="filter-date-to"
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Supplier</Label>
                  <Select value={filters.supplier_id} onValueChange={(v) => setFilters({ ...filters, supplier_id: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="filter-supplier" className="h-9">
                      <SelectValue placeholder="All suppliers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All suppliers</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Item</Label>
                  <Select value={filters.item_id} onValueChange={(v) => setFilters({ ...filters, item_id: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="filter-item" className="h-9">
                      <SelectValue placeholder="All items" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All items</SelectItem>
                      {items.map(i => (
                        <SelectItem key={i.item_id} value={i.item_id}>{i.item_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Quality</Label>
                  <Select value={filters.quality_id} onValueChange={(v) => setFilters({ ...filters, quality_id: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="filter-quality" className="h-9">
                      <SelectValue placeholder="All qualities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All qualities</SelectItem>
                      {qualities.map(q => (
                        <SelectItem key={q.quality_id} value={q.quality_id}>{q.quality_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {showFinishQuality && (
                  <div>
                    <Label className="text-xs">Finish Quality</Label>
                    <Select value={filters.subquality_id} onValueChange={(v) => setFilters({ ...filters, subquality_id: v === 'all' ? '' : v })}>
                      <SelectTrigger data-testid="filter-finish-quality" className="h-9">
                        <SelectValue placeholder="All finish qualities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All finish qualities</SelectItem>
                        {subqualities.map(sq => (
                          <SelectItem key={sq.subquality_id} value={sq.subquality_id}>{sq.subquality_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Order Status</Label>
                  <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                    <SelectTrigger data-testid="filter-status" className="h-9">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Partially Billed">Partially Billed</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {showLRStatus && (
                  <div>
                    <Label className="text-xs">LR Status</Label>
                    <Select value={filters.lr_status_filter} onValueChange={(v) => setFilters({ ...filters, lr_status_filter: v === 'all' ? '' : v })}>
                      <SelectTrigger data-testid="filter-lr-status" className="h-9">
                        <SelectValue placeholder="All LR statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All LR statuses</SelectItem>
                        <SelectItem value="In Transport">In Transport</SelectItem>
                        <SelectItem value="Issued for Delivery">Issued for Delivery</SelectItem>
                        <SelectItem value="Delivered">Delivered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-3">
                <Button data-testid="filter-reset" variant="outline" size="sm" onClick={resetFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default DashboardFilters;
